import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

const Row = ({ ok, label, detail, testId }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testId}>
    {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> : <WarningCircle size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />}
    <div><span className="font-medium">{label}</span>{detail && <span className="text-muted-foreground"> — {detail}</span>}</div>
  </div>
);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

export const UpsHealthCard = () => {
  const [tracking, setTracking] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ups/diagnostics", { params: tracking.trim() ? { tracking: tracking.trim() } : {} });
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnostics failed"); } finally { setBusy(false); }
  };
  const adopt = async () => {
    try { const { data } = await api.post("/ups/adopt-1z-shipments"); toast.success(`${data.updated} shipment(s) marked as UPS`); run(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const lastRun = d?.last_cron_runs?.[0];
  return (
    <div className="hp-card p-6 space-y-4" data-testid="ups-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold">UPS tracking health check</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={adopt} data-testid="ups-adopt-btn">Mark 1Z… shipments as UPS</Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={run} disabled={busy} data-testid="ups-diag-run">
            <ArrowsClockwise size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Checking…" : "Run check"}
          </Button>
        </div>
      </div>
      <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Optional: UPS tracking number to probe (1Z…)" className="h-9" data-testid="ups-diag-tracking" />
      {d && (
        <div className="space-y-2">
          <Row ok={d.configured} label={`Environment: ${d.env}`} detail={d.configured ? `client …${d.client_id_tail}` : "UPS_CLIENT_ID / UPS_CLIENT_SECRET missing"} testId="ups-diag-env" />
          <Row ok={d.auth_ok} label="UPS login (OAuth)" detail={d.auth_ok ? "token issued" : d.auth_error} testId="ups-diag-auth" />
          <Row ok={!!lastRun && lastRun.status === "done" && !lastRun.errors} label="Last auto-sync" detail={lastRun ? `${fmt(lastRun.at)} · checked ${lastRun.checked ?? "?"} · updated ${lastRun.changed ?? 0} · errors ${lastRun.errors ?? 0}${lastRun.last_error ? ` · ${lastRun.last_error}` : ""}` : "no run recorded yet (runs every 2 hours at :30)"} testId="ups-diag-cron" />
          {d.probe && <Row ok={d.probe.found} label={`Tracking probe ${d.probe.tracking}`} detail={d.probe.error || `${d.probe.latest_code || "—"} ${d.probe.latest_status || ""} · ${d.probe.scans} scans`} testId="ups-diag-probe" />}
          <Row ok label={`${d.auto_synced.length} UPS shipment(s) in auto-sync`} testId="ups-diag-count" />
          {d.auto_synced.filter((s) => s.ups?.last_error).map((s) => (
            <Row key={s.id} ok={false} label={<Link to={`/shipments/${s.id}`} className="underline">{s.shipment_number}</Link>} detail={`${s.tracking_number}: ${s.ups.last_error}`} testId={`ups-diag-err-${s.shipment_number}`} />
          ))}
        </div>
      )}
    </div>
  );
};
